import { useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSButton,
  MDSConfigurationListRow,
  MDSConfigurationListRowSlotName,
  MDSIcon,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSRadio,
  MDSRadioGroup,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { useAppStore } from "@/store";
import { IntegrationsLayoutBottomBuffer } from "@/pages/integrations/shared/integrations-layout";
import {
  StyledContentWrapper,
  StyledHeading,
} from "@/pages/integrations/shared/integrations.styles";
import { IntegrationsCalendarHero } from "@/pages/integrations/calendar/components/calendar-hero";
import { CalendarAccountConfigurationList } from "@/components/calendar/calendar-account-configuration-list";
import type { MDSMenuContentList } from "@/mds";
import { resolveDefaultCalendarEventTemplateSyncModelUuid } from "@/modules/uuid/sync-models/resolveDefaultCalendarEventTemplateSyncModelUuid";

export const IntegrationsCalendarPageContent = observer(() => {
  const { store } = useAppStore();
  const googleCalendarStore = store.calendar.google;
  const outlookCalendarStore = store.calendar.outlook;

  useEffect(() => {
    void googleCalendarStore.loadSettings();
  }, [googleCalendarStore]);

  useEffect(() => {
    void outlookCalendarStore.loadSettings();
  }, [outlookCalendarStore]);

  const handleToggleCalendar = useCallback(
    ({
      calendarId,
      isEnabled,
      provider,
      subjectId,
    }: {
      calendarId: string;
      isEnabled: boolean;
      provider: "google" | "outlook";
      subjectId: string;
    }) => {
      if (provider === "google") {
        void googleCalendarStore.toggleCalendar({ subjectId, calendarId, isEnabled });
      } else if (provider === "outlook") {
        void outlookCalendarStore.toggleCalendar({ subjectId, calendarId, isEnabled });
      }
    },
    [googleCalendarStore, outlookCalendarStore]
  );

  const isConnected = googleCalendarStore.isConnected || outlookCalendarStore.isConnected;
  const isUpdating =
    googleCalendarStore.isUpdatingCalendars || outlookCalendarStore.isUpdatingCalendars;

  const createGoogleAccountMenu = useCallback(
    (subjectId: string | null | undefined): MDSMenuContentList => {
      if (!subjectId) return [];
      return [
        {
          id: "remove-account",
          kind: MDSMenuContentItemKind.Item,
          label: "Remove account",
          icon: MDSIconKind.Trash,
          onClick: () => void googleCalendarStore.disconnect(subjectId),
          isDisabled: googleCalendarStore.isDisconnecting,
        },
      ];
    },
    [googleCalendarStore]
  );

  const createOutlookAccountMenu = useCallback(
    (subjectId: string | null | undefined): MDSMenuContentList => {
      if (!subjectId) return [];
      return [
        {
          id: "remove-account",
          kind: MDSMenuContentItemKind.Item,
          label: "Remove account",
          icon: MDSIconKind.Trash,
          onClick: () => void outlookCalendarStore.disconnect(subjectId),
          isDisabled: outlookCalendarStore.isDisconnecting,
        },
      ];
    },
    [outlookCalendarStore]
  );

  const goToDefaultCalendarEventTemplate = useCallback(() => {
    const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId;
    void store.navigation.goToTemplate({
      templateId: resolveDefaultCalendarEventTemplateSyncModelUuid({
        spaceAccountId,
      }),
    });
  }, [store.navigation, store.spaceAccounts.myPersonalSpaceAccountId]);

  return (
    <StyledContentWrapper>
      <IntegrationsCalendarHero isConnected={isConnected} />

      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        CONNECTED CALENDARS
      </StyledHeading>

      <ListStack>
        {googleCalendarStore.accountsList.map(account => (
          <CalendarAccountConfigurationList
            key={account.googleSubjectId}
            email={account.googleAccountEmail}
            provider="google"
            calendars={account.calendars}
            isUpdating={isUpdating}
            lastSyncedAt={account.lastSyncedAt}
            subjectId={account.googleSubjectId ?? undefined}
            onToggleCalendar={handleToggleCalendar}
            accountMenu={createGoogleAccountMenu(account.googleSubjectId)}
          />
        ))}
        {outlookCalendarStore.accountsList.map(account => (
          <CalendarAccountConfigurationList
            key={account.outlookSubjectId}
            email={account.outlookAccountEmail}
            provider="outlook"
            calendars={account.calendars}
            isUpdating={isUpdating}
            lastSyncedAt={account.lastSyncedAt}
            subjectId={account.outlookSubjectId ?? undefined}
            onToggleCalendar={handleToggleCalendar}
            accountMenu={createOutlookAccountMenu(account.outlookSubjectId)}
          />
        ))}

        <MDSConfigurationListRow title="Connect a Google Calendar">
          <MDSIcon
            slot={MDSConfigurationListRowSlotName.LeftAddon}
            kind={MDSIconKind.GoogleCalendar}
          />
          <MDSButton
            slot={MDSConfigurationListRowSlotName.RightAddon}
            onClick={() => void googleCalendarStore.connect()}
            isDisabled={googleCalendarStore.isConnecting}
          >
            Connect
          </MDSButton>
        </MDSConfigurationListRow>

        <MDSConfigurationListRow title="Connect an Outlook Calendar">
          <MDSIcon
            slot={MDSConfigurationListRowSlotName.LeftAddon}
            kind={MDSIconKind.OutlookCalendar}
          />
          <MDSButton
            slot={MDSConfigurationListRowSlotName.RightAddon}
            onClick={() => void outlookCalendarStore.connect()}
            isDisabled={outlookCalendarStore.isConnecting}
          >
            Connect
          </MDSButton>
        </MDSConfigurationListRow>
      </ListStack>

      {isConnected && (
        <ListStack>
          <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            FORMATTING
          </StyledHeading>
          <MDSText variant={MDSTextVariant.ParagraphS}>
            Mem will automatically summarize meetings, adding an informative title and a concise
            summary of key points. You can further customize your note at any time.
          </MDSText>
          <MDSRadioGroup aria-label="Formatting" value="simple" onChange={() => {}}>
            <MDSConfigurationListRow
              title="Templated"
              description="Start all meeting notes with a standard template"
            >
              <MDSRadio
                slot={MDSConfigurationListRowSlotName.LeftAddon}
                value="simple"
                aria-label="Simple formatting"
              />
              <MDSButton
                slot={MDSConfigurationListRowSlotName.RightAddon}
                onClick={goToDefaultCalendarEventTemplate}
              >
                Customize
              </MDSButton>
            </MDSConfigurationListRow>
          </MDSRadioGroup>
        </ListStack>
      )}

      <IntegrationsLayoutBottomBuffer />
    </StyledContentWrapper>
  );
});

const ListStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  max-width: 784px;
`;
